/**
 * SiteLayout - Header y footer coherentes con BaseTemplate
 * Usado por /portfolio, /audiovisual y otras páginas públicas
 * Mantiene consistencia visual con el resto del sitio
 */

import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { BaseTemplateFooter } from "@/components/templates/BaseTemplateFooter";
import { usePortfolioSections } from "@/hooks/usePortfolioSections";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { useTemplateSelection } from "@/hooks/useTemplateSelection";
import { resolveLogoUrl } from "@/lib/cloudinaryUtils";
import { baseTemplateDarkShell } from "@/lib/baseTemplateDarkShell";
import { brandDisplayNameFromSiteTitle } from "@/lib/siteBrandFromSettings";
import { cn } from "@/lib/utils";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";

interface SiteLayoutProps {
  children: React.ReactNode;
}

export function SiteLayout({ children }: SiteLayoutProps) {
  const { settings } = useSiteSettings();
  const { showPortfolioNavLink } = usePortfolioSections();
  const { t } = useTranslation();
  const { language, country, isBaseTemplateBlack } = useTemplateSelection();
  const isDark = isBaseTemplateBlack;
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // headerUrl = destino del clic (/, /portfolio, https://...). Si parece public_id Cloudinary, usar /
  const rawHeaderUrl = settings.headerUrl || "/";
  const headerUrl =
    rawHeaderUrl.startsWith("/") || rawHeaderUrl.startsWith("http")
      ? rawHeaderUrl
      : "/";
  const logoUrl = resolveLogoUrl(settings.logoUrl) ?? settings.logoUrl;
  const brandName = useMemo(
    () => brandDisplayNameFromSiteTitle(settings.title),
    [settings.title],
  );

  const navLinks = useMemo(() => {
    const links = [
      { href: "/", label: t("baseTemplate.nav.home") },
      { href: "/portfolio", label: t("baseTemplate.nav.portfolio") },
      { href: "/#portfolio", label: t("baseTemplate.nav.gallery") },
      { href: "/audiovisual", label: t("baseTemplate.nav.audiovisual") },
      { href: "/#contacto", label: t("baseTemplate.nav.contact") },
    ];
    if (!showPortfolioNavLink) {
      return links.filter((l) => l.href !== "/portfolio");
    }
    return links;
  }, [showPortfolioNavLink, t]);

  const linkIsActive = (href: string) =>
    href === "/"
      ? location.pathname === "/"
      : location.pathname + (location.hash || "") === href;

  return (
    <div
      className={cn(
        "min-h-screen flex flex-col font-body antialiased",
        isDark ? "bg-black text-white" : "bg-white text-black",
      )}
    >
      {/* Header - mismo estilo que BaseTemplate / BaseTemplateBlack */}
      <header
        className={cn(
          isDark
            ? baseTemplateDarkShell.header
            : "sticky top-0 z-50 w-full pt-[env(safe-area-inset-top,0px)] border-b border-neutral-200/90 bg-white shadow-[0_1px_0_0_rgba(255,255,255,1)_inset,0_4px_20px_-8px_rgba(15,23,42,0.06)]",
        )}
      >
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-12 sm:h-16 min-h-[44px]">
            <div className="flex-shrink-0">
              {headerUrl.startsWith("http") ? (
                <a
                  href={headerUrl}
                  className={cn(
                    isDark
                      ? baseTemplateDarkShell.logoLink
                      : "inline-flex items-center gap-2 text-lg sm:text-xl font-display font-medium whitespace-nowrap tracking-wider transition-opacity text-black hover:opacity-80",
                  )}
                  aria-label={brandName}
                >
                  {logoUrl ? (
                    <img
                      src={logoUrl}
                      alt={brandName}
                      className="h-8 sm:h-10 w-auto max-h-10 object-contain object-center"
                      loading="eager"
                      fetchPriority="high"
                    />
                  ) : (
                    brandName
                  )}
                </a>
              ) : (
                <Link
                  to={headerUrl || "/"}
                  className={cn(
                    isDark
                      ? baseTemplateDarkShell.logoLink
                      : "inline-flex items-center gap-2 text-lg sm:text-xl font-display font-medium whitespace-nowrap tracking-wider transition-opacity text-black hover:opacity-80",
                  )}
                  aria-label={brandName}
                >
                  {logoUrl ? (
                    <img
                      src={logoUrl}
                      alt={brandName}
                      className="h-8 sm:h-10 w-auto max-h-10 object-contain object-center"
                      loading="eager"
                      fetchPriority="high"
                    />
                  ) : (
                    brandName
                  )}
                </Link>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 flex-shrink-0">
              <nav
                className="hidden md:flex space-x-8 mr-2"
                aria-label="Main navigation"
              >
                {navLinks.map((link) => {
                  const isActive = linkIsActive(link.href);
                  return (
                    <Link
                      key={link.href}
                      to={link.href}
                      className={cn(
                        isDark
                          ? isActive
                            ? baseTemplateDarkShell.navDesktopLinkActive
                            : baseTemplateDarkShell.navDesktopLink
                          : isActive
                            ? "font-body text-black transition-colors duration-300 font-medium focus:outline-none rounded tracking-wide text-sm focus:ring-2 focus:ring-black/20"
                            : "font-body text-black/60 hover:text-black transition-colors duration-300 font-medium focus:outline-none rounded tracking-wide text-sm focus:ring-2 focus:ring-black/20",
                      )}
                    >
                      {link.label}
                    </Link>
                  );
                })}
              </nav>
              <LanguageSwitcher variant={isDark ? "dark" : "light"} />
              <div className="md:hidden">
                <button
                  type="button"
                  aria-label="Toggle navigation menu"
                  aria-expanded={mobileMenuOpen}
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className={cn(
                    isDark ? baseTemplateDarkShell.menuButton : "text-black/60 hover:text-black focus:outline-none focus:ring-2 focus:ring-black/20 rounded",
                  )}
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        {mobileMenuOpen && (
          <div
            className={cn(
              isDark
                ? baseTemplateDarkShell.mobileMenuPanel
                : "md:hidden border-t border-black/10 bg-white",
            )}
          >
            <div className="px-2 pt-2 pb-3 space-y-1">
              {navLinks.map((link) => {
                const isActive = linkIsActive(link.href);
                return (
                  <Link
                    key={link.href}
                    to={link.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className={cn(
                      isDark
                        ? isActive
                          ? `${baseTemplateDarkShell.mobileNavLink} text-white`
                          : baseTemplateDarkShell.mobileNavLink
                        : "font-body block px-3 py-2 font-medium tracking-wide transition-colors duration-300 " +
                          (isActive ? "text-black" : "text-black/60 hover:text-black"),
                    )}
                  >
                    {link.label}
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </header>

      {/* Main — mismo id que BaseTemplate / BaseTemplateBlack */}
      <main id="main-content" className="flex-1 relative">
        {children}
      </main>

      <BaseTemplateFooter
        variant={isDark ? "dark" : "light"}
        language={language}
        contactHref="/contacto"
        analyticsTemplate={isDark ? "BaseTemplateBlack" : "BaseTemplate"}
        countryCode={country}
        brandName={brandName}
        footerTagline={settings.footerTagline}
        showPortfolioNavLink={showPortfolioNavLink}
      />
    </div>
  );
}
